/** Download bookkeeping code for the project task report (not a /reports catalogue code). */
export const PROJECT_TASK_REPORT_CODE = 'PROJECT_TASK_REPORT';

/** Periods the API resolves against the current business date. */
export type ProjectReportPeriod =
  | 'TODAY'
  | 'YESTERDAY'
  | 'THIS_WEEK'
  | 'LAST_WEEK'
  | 'THIS_MONTH'
  | 'LAST_MONTH'
  | 'CUSTOM';

/** One query drives both View and Download so their rows always match. */
export type ProjectTaskReportQuery = {
  period: ProjectReportPeriod;
  /** Only sent for CUSTOM; presets are resolved by the API. */
  fromDate?: string | null;
  toDate?: string | null;
  /** null = all employees the signed-in user may access in this project. */
  employeeId: number | null;
  /** The opened project; every report is for exactly one project. */
  projectId: number;
};

export type ProjectReportEmployee = {
  employeeId: number;
  employeeNumber: string | null;
  displayName: string;
};

export type ProjectReportEmployeeOptions = {
  /** false for employees: the API only ever returns their own report. */
  canSelectAllEmployees: boolean;
  items: ProjectReportEmployee[];
};

export type ProjectTaskReportSubtaskAssignee = {
  employeeId: number;
  employeeName: string;
  memberRole: string | null;
};

/** A live subtask of the row's task (prj_subtask), in board order. */
export type ProjectTaskReportSubtask = {
  subtaskId: number;
  subtaskName: string;
  isCompleted: boolean;
  assignees: ProjectTaskReportSubtaskAssignee[];
};

/** One of the row employee's time entries inside the report period. */
export type ProjectTaskReportTimeEntry = {
  timeLogId: number;
  date: string | null;
  /** "HH:mm", 24h. */
  startTime: string | null;
  endTime: string | null;
  durationMinutes: number;
};

export type ProjectTaskReportRow = {
  projectId: number;
  projectName: string;
  taskId: number;
  taskName: string;
  employeeId: number;
  employeeNumber: string | null;
  employeeName: string;
  taskStatus: string | null;
  isCompleted: boolean;
  /** The task's creation date (prj_task.created_at), shown as "Created date". */
  taskDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  timeEstimateMinutes: number | null;
  /** The employee's own time logs inside the selected range. */
  spentMinutesInRange: number;
  /** Task total across everyone (prj_task.time_spent_minutes). */
  timeSpentMinutes: number;
  firstWorkedOn: string | null;
  lastWorkedOn: string | null;
  timeLogCount: number;
  subtaskTotal: number;
  subtaskCompleted: number;
  createdByName: string | null;
  description: string | null;
  subtasks: ProjectTaskReportSubtask[];
  /** Oldest first; empty when the API predates time entries in reports. */
  timeEntries: ProjectTaskReportTimeEntry[];
};

export type ProjectTaskReportEmployeeSummary = {
  employeeId: number;
  employeeNumber: string | null;
  employeeName: string;
  taskCount: number;
  completedCount: number;
  spentMinutesInRange: number;
};

export type ProjectTaskReportSummary = {
  rowCount: number;
  taskCount: number;
  projectCount: number;
  employeeCount: number;
  completedCount: number;
  spentMinutesInRange: number;
};

export type ProjectTaskReport = {
  generatedAt: string | null;
  project: { projectId: number; projectName: string };
  filters: {
    period: ProjectReportPeriod;
    fromDate: string;
    toDate: string;
    employeeId: number | null;
    employeeName: string | null;
  };
  summary: ProjectTaskReportSummary;
  employees: ProjectTaskReportEmployeeSummary[];
  rows: ProjectTaskReportRow[];
};

export type ProjectTaskReportSection = ProjectTaskReportEmployeeSummary & {
  rows: ProjectTaskReportRow[];
};
